import { Tabs, Tooltip } from '@pmi/dsm-react';
import { Label } from '@pmi/dsm-react/dist/components/tabs/Tabs';
import {
  AlertErrorSolidIcon,
  AlertWarningSolidIcon,
} from '@pmi/dsm-react/dist/components/icons';
import {
  AllDeal,
  AgreementApiModel,
  DealOverview,
} from '../../models/DealModel';
import sortDeals from '../../utils/sortDeals';
import checkExpiryStatus from '../../utils/checkExpiryStatus';

interface DealTabProps {
  deals: DealOverview[] | AgreementApiModel[];
  selectedDeal: AgreementApiModel;
  showAllSelector?: boolean;
  pinPayByUser?: boolean;
  onTabChange: (deal: AgreementApiModel) => void;
}

export default function DealTab({
  deals,
  selectedDeal,
  onTabChange,
  showAllSelector = false,
  pinPayByUser = false,
}: DealTabProps) {
  const sortedDeals = sortDeals(deals, 'id', pinPayByUser) ?? [];
  const allDeals = showAllSelector ? [AllDeal, ...sortedDeals] : sortedDeals;

  const tabLabels: Label[] = sortedDeals.map((deal) => {
    const { isExpiring, isExpired } = checkExpiryStatus(deal.expiration);
    const isDisabled = !deal.enabled;

    const renderIconAndTooltip = () => {
      // priority is expired > disabled > expiring soon
      if (isExpired) {
        return {
          icon: (
            <AlertWarningSolidIcon
              color="danger"
              fontSize="sm"
              className="me-1"
            />
          ),
          tooltipContent: 'Agreement is expired.',
        };
      }
      if (isDisabled) {
        return {
          icon: (
            <AlertErrorSolidIcon color="danger" fontSize="sm" className="me-1" />
          ),
          tooltipContent: 'Agreement is disabled.',
        };
      }
      if (isExpiring) {
        return {
          icon: (
            <AlertWarningSolidIcon
              color="warning"
              fontSize="sm"
              className="me-1"
            />
          ),
          tooltipContent: 'Agreement is expiring soon.',
        };
      }

      return { icon: null, tooltipContent: null };
    };

    const { icon, tooltipContent } = renderIconAndTooltip();
    const labelContent = (
      <div className="d-flex" key={`dealTab-${deal.id}`}>
        {tooltipContent && (
          <Tooltip content={tooltipContent} className="darkTooltip" placement="top">
            <div>{icon}</div>
          </Tooltip>
        )}
        <div>{!deal.dealName ? `Agreement ID: ${deal.id}` : deal.dealName}</div>
      </div>
    );
    return {
      label: labelContent,
      value: deal.id,
    };
  });

  if (showAllSelector) {
    tabLabels.unshift({ label: 'All Agreements', value: AllDeal.id });
  }

  const updatedValue = tabLabels?.findIndex(
    (d) => d.value === selectedDeal?.id,
  );

  return (
    <div className="deal-tabs">
      <Tabs
        labels={tabLabels}
        theme="light"
        value={updatedValue}
        onChange={(evt, value) => {
          const selectedId = tabLabels[value].value;
          onTabChange(allDeals.find((deal) => deal.id === selectedId));
        }}
        key="deal-tabs"
      />
      <hr className="m-0" />
    </div>
  );
}
